import i18n from 'i18next';
import config from '../config';
import joinList from '../utils/joinList';

let LAST_NOTIFICATION: undefined | { title: string, data: number };

const NOTIF_TAG = 'cogneed-app-notification';

let IS_WINDOW_HIDDEN = typeof document !== 'undefined' ? document.hidden : false;
let IS_VISIBILITY_TRACKING_INITIALIZED = false;

type BrowserNotificationPayload = {
  title: string;
  options?: NotificationOptions;
};

type NotificationActionPayload = {
  action: string;
  title: string;
};

type NotificationPayload = {
  title: string;
  body?: string;
  actions?: NotificationActionPayload[];
};

export type BrowserNotificationEvent =
  | { type: 'external-call-started' }
  | { type: 'detection', cardTitle?: string, commandTitle?: string }
  | { type: 'new-compliance-task', title: string, commands?: string[], instructions?: string }
  | { type: 'rejected-compliance-task', title: string, command: string }
  | { type: 'compliance-reminder', title: string };

function t(key: string, options?: Record<string, unknown>) {
  return i18n.t(key, { ns: 'notifications', ...options });
}

function getNotificationPayload(event: BrowserNotificationEvent): NotificationPayload {
  switch (event.type) {
    case 'external-call-started':
      return {
        title: t('notification.new-session.title'),
        body: t('notification.new-session.body'),
      };
    case 'detection':
      return {
        title: t('new-detection.title', { title: event.cardTitle }),
        body: t('new-detection.body', { title: event.commandTitle }),
        actions: [{ action: 'open', title: t('new-detection.open') }],
      };
    case 'new-compliance-task': {
      let body: string | undefined;
      if (event.commands?.length) {
        body = t('new-compliance-task.body', {
          commands: joinList(event.commands.map((command) => `"${command}"`), i18n.t('or', { ns: 'common' })),
        });
      }
      if (event.instructions) {
        body = `"${event.instructions}"`;
      }
      return {
        title: t('new-compliance-task.title', { title: event.title }),
        body,
        actions: [{ action: 'open', title: t('new-compliance-task.open') }],
      };
    }
    case 'rejected-compliance-task':
      return {
        title: t('rejected-compliance-task.title', { title: event.title, command: event.command }),
        actions: [{ action: 'open', title: t('notification-open') }],
      };
    case 'compliance-reminder':
      return {
        title: t('reminder-compliance-task.title', { title: event.title }),
        body: t('reminder-compliance-task.body', { title: event.title }),
        actions: [{ action: 'open', title: t('notification-open') }],
      };
    default:
      throw new Error(`Unsupported notification event: ${(event as BrowserNotificationEvent).type}`);
  }
}

function toBrowserNotificationPayload(payload: NotificationPayload): BrowserNotificationPayload {
  return {
    title: payload.title,
    options: {
      body: payload.body,
      actions: payload.actions,
    },
  };
}

function toElectronNotificationPayload(payload: NotificationPayload) {
  return {
    title: payload.title,
    body: payload.body,
    silent: true,
  };
}

function updateIsHidden() {
  IS_WINDOW_HIDDEN = false;
}

function onVisibilityChange() {
  IS_WINDOW_HIDDEN = document.hidden;
}

function ensureVisibilityTracking() {
  if (IS_VISIBILITY_TRACKING_INITIALIZED || typeof window === 'undefined') return;

  IS_WINDOW_HIDDEN = document.hidden;
  window.addEventListener('focus', updateIsHidden);
  window.addEventListener('mousemove', updateIsHidden);
  window.addEventListener('mouseout', updateIsHidden);
  window.addEventListener('mouseover', updateIsHidden);
  document.addEventListener('visibilitychange', onVisibilityChange);
  IS_VISIBILITY_TRACKING_INITIALIZED = true;
}

function hasDualChannelApis() {
  return !!(
    window.appStateAPI
    || window.appStateBridge
    || window.electronMessagingApi
    || window.electronMessagingBridge
    || window.electronNotificationAPI
    || window.electronNotificationBridge
    || window.audioDeviceAPI
    || window.audioDeviceBridge
    || window.audioAPI
    || window.audioBridge
  );
}

function hasNotificationPermission() {
  return typeof Notification !== 'undefined' && Notification.permission === 'granted';
}

function isInCoolDown(title: string) {
  return !!LAST_NOTIFICATION && LAST_NOTIFICATION.title === title && LAST_NOTIFICATION.data > Date.now() - config.NotificationCoolDown;
}

function rememberNotification(title: string) {
  LAST_NOTIFICATION = { title, data: Date.now() };
}

async function sendWebNotification(payload: NotificationPayload) {
  if (!hasNotificationPermission()) return;
  ensureVisibilityTracking();
  if (!IS_WINDOW_HIDDEN) return;

  const { title, options } = toBrowserNotificationPayload(payload);
  const registration = await navigator.serviceWorker.ready;
  if (isInCoolDown(title)) {
    return;
  }

  const notifications = await registration.getNotifications();
  const notification = notifications.find((current) => current.tag === NOTIF_TAG);
  if (notification) {
    notification.close();
  }

  registration.showNotification(title, {
    icon: 'logo192.png',
    silent: true,
    tag: NOTIF_TAG,
    ...options,
  });
  rememberNotification(title);
}

async function sendElectronNotification(payload: NotificationPayload) {
  const electronNotificationAPI = window.electronNotificationAPI ?? window.electronNotificationBridge;
  if (!electronNotificationAPI) return;

  const isSupported = await electronNotificationAPI.isNotificationSupported();
  if (!isSupported) return;

  const isLiveWindowHidden = await electronNotificationAPI.isLiveWindowHidden();
  if (!isLiveWindowHidden) return;

  if (isInCoolDown(payload.title)) return;

  const didShowNotification = await electronNotificationAPI.showNotification(toElectronNotificationPayload(payload));
  if (didShowNotification) {
    rememberNotification(payload.title);
  }
}

export default async function sendNotification(event: BrowserNotificationEvent) {
  const payload = getNotificationPayload(event);

  if (hasDualChannelApis()) {
    await sendElectronNotification(payload);
    return;
  }

  await sendWebNotification(payload);
}
